<template>
  <div>
    <Table
      resource="Sale"
      :actions="actions"
      :items="$store.getters['saleV2/tableData']"
      :detail="$store.getters['saleV2/detailData']"
      :fields="$store.getters['saleV2/tableFields']"
      :pagination="$store.state.saleV2.tablePagination"
      :keyword="$store.state.saleV2.tableKeyword"
      :loading="$store.state.saleV2.loadingTable"
      :filter="$store.state.saleV2.tableFilter"
      :sort="$store.state.saleV2.tableSort"
      :sort-options="sortOptions"
      @changeFields="changeFields"
      @resetFields="resetFields"
      @filter="filterData"
      @sort="sortData"
      @load="loadData"
      @delete="deleteData"
      @loadDetail="loadDetail"
      @resetDetail="resetDetail"
      @paginate="paginate"
      @search="search"
    />

    <b-modal
      v-model="duplicateModalVisible"
      title="Duplikasi Sale"
      ok-title="Duplikasi"
      cancel-title="Batal"
      ok-variant="primary"
      :ok-disabled="duplicateSubmitDisabled"
      :no-close-on-backdrop="$store.state.saleV2.loadingSubmit"
      :no-close-on-esc="$store.state.saleV2.loadingSubmit"
      @ok="submitDuplicateSale"
      @hidden="resetDuplicateForm"
    >
      <template #modal-ok>
        <div class="position-relative d-flex align-items-center justify-content-center">
          <span :class="{ 'invisible': $store.state.saleV2.loadingSubmit }">
            Duplikasi
          </span>
          <b-spinner
            v-if="$store.state.saleV2.loadingSubmit"
            small
            class="position-absolute"
          />
        </div>
      </template>

      <b-form-group
        label="Nama Sale"
        label-for="duplicate-sale-title"
      >
        <b-form-input
          id="duplicate-sale-title"
          v-model.trim="duplicateForm.title"
          placeholder="Masukkan nama sale"
          :disabled="$store.state.saleV2.loadingSubmit"
        />
      </b-form-group>

      <div class="row">
        <div class="col-md-6">
          <b-form-group
            label="Tanggal Mulai"
            label-for="duplicate-sale-start"
          >
            <b-input-group class="input-group-merge">
              <b-input-group-prepend is-text>
                <feather-icon icon="CalendarIcon" />
              </b-input-group-prepend>
              <flat-pickr
                id="duplicate-sale-start"
                v-model="duplicateForm.shouldStartedAt"
                class="form-control"
                placeholder="Pilih tanggal & waktu"
                :config="datePickrConfig"
                :disabled="$store.state.saleV2.loadingSubmit"
              />
            </b-input-group>
          </b-form-group>
        </div>

        <div class="col-md-6">
          <b-form-group
            label="Tanggal Selesai"
            label-for="duplicate-sale-end"
          >
            <b-input-group class="input-group-merge">
              <b-input-group-prepend is-text>
                <feather-icon icon="CalendarIcon" />
              </b-input-group-prepend>
              <flat-pickr
                id="duplicate-sale-end"
                v-model="duplicateForm.shouldEndedAt"
                class="form-control"
                placeholder="Pilih tanggal & waktu"
                :config="datePickrConfig"
                :disabled="$store.state.saleV2.loadingSubmit"
              />
            </b-input-group>
          </b-form-group>
        </div>
      </div>

      <small
        v-if="dateValidationError"
        class="text-danger d-block mb-1"
      >
        {{ dateValidationError }}
      </small>

    </b-modal>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormInput,
  BModal,
  BInputGroup,
  BInputGroupPrepend,
  BSpinner,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import Table from '@/layouts/components/v2/Table.vue'

export default {
  components: {
    BFormGroup,
    BFormInput,
    BModal,
    BInputGroup,
    BInputGroupPrepend,
    BSpinner,
    Table,
    flatPickr,
  },
  data() {
    return {
      duplicateModalVisible: false,
      duplicateSaleSource: null,
      duplicateForm: {
        title: '',
        shouldStartedAt: '',
        shouldEndedAt: '',
      },
      dateValidationError: '',
      datePickrConfig: {
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
      },

      // Sort
      sortOptions: [
        {
          key: 'created_at', direction: 'desc', label: 'Terbaru', default: true,
        },
        { key: 'created_at', direction: 'asc', label: 'Terlama' },
      ],

      actions: [
        'create',
        'view',
        {
          type: 'update',
          visibility: {
            callback: data => data.item && (this.isAdmin || data.item.is_owner),
          },
        },
        {
          type: 'delete',
          visibility: {
            callback: data => data.item && (this.isAdmin || data.item.is_owner),
          },
        },
        {
          type: 'other',
          children: [
            {
              label: 'Ubah Harga',
              event: data => this.navigateToPrice(data.item),
              visibility: {
                callback: data => data.item && data.item.is_involved && !data.item.is_owner,
              },
            },
            {
              label: 'Duplikasi Sale',
              event: data => this.openDuplicateSaleModal(data.item),
              visibility: {
                callback: data => data.item && (this.isAdmin || data.item.is_owner),
              },
            },
          ],
        },
      ],
    }
  },
  computed: {
    userData() {
      return this.$store.state.auth.userData
    },
    isAdmin() {
      return this.userData?.role === 'admin'
    },
    duplicateSubmitDisabled() {
      return this.$store.state.saleV2.loadingSubmit || !this.duplicateForm.title || this.dateValidationError !== ''
    },
  },
  watch: {
    'duplicateForm.shouldStartedAt': 'validateDateStart',
    'duplicateForm.shouldEndedAt': 'validateDateEnd',
  },
  methods: {
    validateDateStart() {
      const start = this.duplicateForm.shouldStartedAt
      const end = this.duplicateForm.shouldEndedAt
      if (start && end && new Date(start) >= new Date(end)) {
        this.dateValidationError = 'Tanggal mulai harus lebih awal dari tanggal selesai.'
      } else {
        this.dateValidationError = ''
      }
    },
    validateDateEnd() {
      const start = this.duplicateForm.shouldStartedAt
      const end = this.duplicateForm.shouldEndedAt
      if (start && end && new Date(start) >= new Date(end)) {
        this.dateValidationError = 'Tanggal mulai harus lebih awal dari tanggal selesai.'
      } else {
        this.dateValidationError = ''
      }
    },
    loadData() {
      return this.$store.dispatch('saleV2/getTableData')
    },
    loadDetail(id) {
      return this.$store.dispatch('saleV2/getDetail', id)
    },
    deleteData(id, callback) {
      return this.$store.dispatch('saleV2/delete', id)
        .then(() => {
          callback()
        })
        .catch(err => {
          callback(err)
        })
    },
    resetDetail() {
      this.$store.commit('saleV2/SET_DETAIL_DATA', undefined)
    },
    changeFields(val) {
      this.$store.commit('saleV2/SET_TABLE_FIELDS', val)
    },
    resetFields() {
      this.$store.commit('saleV2/RESET_TABLE_FIELDS')
    },
    filterData(val) {
      this.$store.commit('saleV2/SET_TABLE_FILTER', val)
    },
    sortData(val) {
      this.$store.commit('saleV2/SET_TABLE_SORT', val)
    },
    paginate(val) {
      this.$store.commit('saleV2/SET_TABLE_PAGINATION', val)
    },
    search(val) {
      this.$store.commit('saleV2/SET_TABLE_KEYWORD', val)
    },
    navigateToPrice(saleItem) {
      // Get brand slug from current user's merchant data
      const brandSlug = this.userData?.merchant?.slug
      const saleId = saleItem.id

      if (!brandSlug) {
        return
      }

      // Navigate to price page
      const priceUrl = `/sale/${saleId}/brand/${brandSlug}/price`
      this.$router.push(priceUrl)
    },
    openDuplicateSaleModal(saleItem) {
      this.duplicateSaleSource = saleItem
      this.duplicateForm = {
        title: this.generateCopyTitle(saleItem.title),
        shouldStartedAt: '',
        shouldEndedAt: '',
      }
      this.duplicateModalVisible = true
    },
    resetDuplicateForm() {
      if (this.$store.state.saleV2.loadingSubmit) return

      this.duplicateSaleSource = null
      this.duplicateForm = {
        title: '',
        shouldStartedAt: '',
        shouldEndedAt: '',
      }
      this.dateValidationError = ''
    },
    generateCopyTitle(title) {
      if (!title) return 'Sale Copy'

      const matches = title.match(/^(.+)\s+Copy(?:\s+(\d+))?$/)
      if (!matches) return `${title} Copy`

      const nextCopyNumber = matches[2] ? Number(matches[2]) + 1 : 2
      return `${matches[1]} Copy ${nextCopyNumber}`
    },
    async submitDuplicateSale(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (!this.duplicateSaleSource || !this.duplicateForm.title) return

      try {
        const payload = {
          title: this.duplicateForm.title,
          should_started_at: this.duplicateForm.shouldStartedAt || null,
          should_ended_at: this.duplicateForm.shouldEndedAt || null,
        }

        const response = await this.$store.dispatch('saleV2/duplicate', {
          id: this.duplicateSaleSource.id,
          payload,
        })

        this.duplicateModalVisible = false

        this.$swal({
          title: 'Berhasil!',
          text: 'Sale berhasil diduplikat.',
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'Lihat Sale',
          cancelButtonText: 'Tutup',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-secondary ml-1',
          },
          buttonsStyling: false,
        }).then(result => {
          if (result.isConfirmed && response.data) {
            // Navigate to the newly created sale edit page
            this.$router.push(`/sale/${response.data.id}/edit`)
          }
        })

        // Always reload table data to show the new duplicated sale
        this.loadData()
      } catch (error) {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Gagal',
            icon: 'XIcon',
            text: 'Gagal menduplikasi sale',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>
